import { useEffect, useMemo, useState } from "react";
import { collection, doc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { Link, useLocation, useRoute } from "wouter";
import { ArrowLeft, FileText } from "lucide-react";
import { paths, ROLE_RANK, SURCHARGE_LABEL, type Expense } from "@clevis/shared-types";
import { Button, Card, ConfirmButton, Input, cn } from "@clevis/shared-ui";
import { db, storage } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useEntitlements } from "../../hooks/useEntitlements";
import { ExpenseEditor, emptyLine, money, type ExpenseDraft, type JobOption } from "../../components/ExpenseEditor";
import { ExpenseQbPanel } from "../../components/ExpenseQbPanel";
import { ReceiveByInvoice } from "../../components/ReceiveByInvoice";
import { saveExpense } from "../../lib/expenseScan";

// Expenses: what the company spent, by job. Supplier invoices from the
// receiving dock, techs' receipts and office entries land here; managers
// approve drafts and send them to QuickBooks as Bills. Dense rows, one
// filter strip that never scrolls sideways, and every QuickBooks state
// says what it means.

export type ExpenseRow = Expense & { id: string };
type Filter = "all" | "approval" | "notInQb" | "attention";
const FILTERS: Array<[Filter, string]> = [
  ["all", "All"],
  ["approval", "Needs approval"],
  ["notInQb", "Not in QuickBooks"],
  ["attention", "Needs attention"],
];

const fmtDay = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "";

export function qbChip(e: Expense): { label: string; style: string } | null {
  const s = e.qbd ?? e.qbo;
  if (!s) return null;
  if (s.status === "synced") return { label: "In QuickBooks", style: "bg-success/15 text-success" };
  if (s.status === "queued") return { label: "Sending…", style: "bg-brandorange/15 text-brandorange-dark" };
  return { label: "QuickBooks: fix", style: "bg-danger/15 text-danger" };
}

export function toDraft(e: Expense): ExpenseDraft {
  return {
    vendorName: e.vendorName,
    invoiceNumber: e.invoiceNumber,
    invoiceDate: e.invoiceDate ? new Date(e.invoiceDate).toISOString().slice(0, 10) : null,
    jobId: e.jobId,
    lines: e.lines.map((l) => ({ ...l })),
    surcharges: e.surcharges.map((s) => ({ ...s })),
    note: e.note,
  };
}

function useJobOptions(tenantId: string | null): JobOption[] {
  const [jobs, setJobs] = useState<JobOption[]>([]);
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      query(collection(db, paths.tenantJobs(tenantId)), orderBy("createdAt", "desc"), limit(400)),
      (snap) =>
        setJobs(
          snap.docs
            .filter((d) => d.get("status") !== "canceled")
            .map((d) => ({ id: d.id, title: (d.get("title") as string) ?? "Job" })),
        ),
    );
  }, [tenantId]);
  return jobs;
}

export function ExpensesPage() {
  const { tenantId, role, superAdmin } = useAuth();
  const aiReceiving = useEntitlements().hasFeature("invoiceToInventory");
  const rank = superAdmin ? 99 : role ? ROLE_RANK[role] : -1;
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  // /expenses/:id opens one (jobs link straight to their expenses).
  const [, params] = useRoute("/expenses/:id");
  const [, navigate] = useLocation();
  const openId = params?.id ?? null;
  const setOpenId = (id: string | null) => navigate(id ? `/expenses/${id}` : "/expenses");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const jobs = useJobOptions(tenantId);
  const jobTitle = useMemo(() => new Map(jobs.map((j) => [j.id, j.title])), [jobs]);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      query(collection(db, paths.tenantExpenses(tenantId)), orderBy("createdAt", "desc"), limit(500)),
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Expense) }))),
    );
  }, [tenantId]);

  const visible = rows.filter((e) => {
    const s = e.qbd ?? e.qbo;
    if (filter === "approval" && e.status !== "draft") return false;
    if (filter === "notInQb" && (e.status !== "approved" || s)) return false;
    if (filter === "attention" && !(s && (s.status === "error" || s.status === "unconfirmed"))) return false;
    if (!search) return true;
    const hay = `${e.number} ${e.vendorName} ${e.invoiceNumber} ${e.jobIds.map((j) => jobTitle.get(j) ?? "").join(" ")}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  const open = rows.find((r) => r.id === openId) ?? null;
  if (open && tenantId) {
    return <ExpenseDetail tenantId={tenantId} expense={open} jobs={jobs} rank={rank} onBack={() => setOpenId(null)} />;
  }

  async function addManual(d: ExpenseDraft) {
    if (!tenantId) return;
    setBusy(true);
    setMessage("");
    try {
      const res = await saveExpense(tenantId, d, { source: "manual" });
      setAdding(false);
      setMessage(`Expense ${res.number} saved${res.status === "draft" ? " for a manager to approve" : ""}.`);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Expenses</h1>
        <Button onClick={() => setAdding((a) => !a)}>{adding ? "Close" : "Add expense"}</Button>
      </div>

      {adding ? (
        <Card>
          <ExpenseEditor
            initial={{ vendorName: "", invoiceNumber: "", invoiceDate: new Date().toISOString().slice(0, 10), jobId: null, lines: [emptyLine()], surcharges: [], note: "" }}
            jobs={jobs}
            showHandling={rank >= ROLE_RANK.manager}
            busy={busy}
            onSave={(d) => void addManual(d)}
            onCancel={() => setAdding(false)}
          />
        </Card>
      ) : null}
      {rank >= ROLE_RANK.manager && tenantId && !adding && aiReceiving ? <ReceiveByInvoice tenantId={tenantId} /> : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
          {FILTERS.map(([f, label]) => (
            <button
              key={f}
              className={cn("rounded-md px-2.5 py-1", filter === f ? "bg-ink text-white" : "text-steel hover:text-ink")}
              onClick={() => setFilter(f)}
            >
              {label}
            </button>
          ))}
        </div>
        <Input className="min-w-40 flex-1 py-1.5" placeholder="Vendor, number, job…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {message ? <p className="text-xs text-ink">{message}</p> : null}

      <Card className="p-0">
        {visible.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-steel">
            {rows.length === 0
              ? "No expenses yet. Upload a supplier invoice above, add one by hand, or have techs add receipts from their jobs."
              : "Nothing matches this filter."}
          </p>
        ) : (
          visible.map((e) => {
            const chip = qbChip(e);
            const titles = e.jobIds.map((j) => jobTitle.get(j)).filter(Boolean);
            return (
              <button
                key={e.id}
                className="flex w-full items-center justify-between gap-3 border-b border-steel/10 px-4 py-2 text-left last:border-0 hover:bg-cream/60"
                onClick={() => setOpenId(e.id)}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">
                    #{e.number} · {e.vendorName}
                  </div>
                  <div className="truncate text-xs text-steel">
                    {[fmtDay(e.invoiceDate ?? e.createdAt), e.invoiceNumber ? `inv ${e.invoiceNumber}` : "", titles.length === 1 ? titles[0] : titles.length > 1 ? `${titles.length} jobs` : "no job"]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {e.status === "draft" ? (
                    <span className="rounded-full bg-steel/15 px-2 py-0.5 text-xs text-steel">needs approval</span>
                  ) : null}
                  {chip ? <span className={cn("rounded-full px-2 py-0.5 text-xs", chip.style)}>{chip.label}</span> : null}
                  <span className="w-20 text-right text-sm text-ink">{money(e.totalCents)}</span>
                </div>
              </button>
            );
          })
        )}
      </Card>
    </div>
  );
}

export function ExpenseDetail({
  tenantId,
  expense,
  jobs,
  rank,
  onBack,
}: {
  tenantId: string;
  expense: ExpenseRow;
  jobs: JobOption[];
  rank: number;
  onBack: () => void;
}) {
  const { user } = useAuth();
  const { hasIntegration } = useEntitlements();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [live, setLive] = useState<ExpenseRow>(expense);
  const jobTitle = new Map(jobs.map((j) => [j.id, j.title]));
  const isManager = rank >= ROLE_RANK.manager;
  const isAdmin = rank >= ROLE_RANK.admin;
  const mine = live.createdByUid === user?.uid;
  const canEdit = isManager || (mine && live.status === "draft");
  const inQb = live.pushes.some((p) => p.status === "synced" || p.status === "queued");
  const targets = (["desktop", "online"] as const).filter((t) =>
    hasIntegration(t === "desktop" ? "quickbooks-desktop" : "quickbooks-online"),
  );

  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenantExpense(tenantId, expense.id)), (snap) => {
        if (snap.exists()) setLive({ id: snap.id, ...(snap.data() as Expense) });
      }),
    [tenantId, expense.id],
  );

  async function act(fn: string, data: Record<string, unknown>, after?: () => void) {
    setBusy(true);
    setMessage("");
    try {
      await call(fn, { tenantId, expenseId: live.id, ...data });
      after?.();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function openDocument() {
    if (!live.documentPath) return;
    try {
      window.open(await getDownloadURL(storageRef(storage, live.documentPath)), "_blank", "noopener");
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <button className="inline-flex items-center gap-1 text-sm text-steel hover:text-ink" onClick={onBack}>
        <ArrowLeft size={14} /> Expenses
      </button>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-lg font-bold text-ink">
              Expense {live.number} · {live.vendorName}
            </h1>
            <p className="text-xs text-steel">
              {[
                fmtDay(live.invoiceDate),
                live.invoiceNumber ? `invoice ${live.invoiceNumber}` : "",
                live.source === "receipt" ? "receipt from the field" : live.source === "receiving" ? "from the receiving dock" : live.source === "referral" ? "referral reward (Clevis Relay)" : "entered by hand",
                live.createdByName ? `by ${live.createdByName}` : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {live.documentPath ? (
              <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => void openDocument()}>
                <FileText size={13} /> Document
              </Button>
            ) : null}
            {live.status === "draft" && isManager ? (
              <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void act("approveExpense", {})}>
                Approve
              </Button>
            ) : null}
            {live.status === "draft" ? <span className="rounded-full bg-steel/15 px-2 py-0.5 text-xs text-steel">needs approval</span> : null}
          </div>
        </div>

        {editing ? (
          <div className="mt-3">
            <ExpenseEditor
              initial={toDraft(live)}
              jobs={jobs}
              showHandling={isManager}
              busy={busy}
              saveLabel="Save changes"
              onCancel={() => setEditing(false)}
              onSave={(d) =>
                void act(
                  "updateExpense",
                  {
                    vendorName: d.vendorName,
                    invoiceNumber: d.invoiceNumber,
                    invoiceDate: d.invoiceDate,
                    jobId: d.jobId,
                    lines: d.lines,
                    surcharges: d.surcharges,
                    note: d.note,
                  },
                  () => setEditing(false),
                )
              }
            />
            {inQb ? (
              <p className="mt-2 text-xs text-steel">
                Changes here don't change QuickBooks until you send the expense again.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="mt-3 text-sm">
            <div className="divide-y divide-steel/10">
              {live.lines.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-ink">
                      {l.quantity !== 1 ? `${l.quantity} × ` : ""}
                      {l.description}
                    </div>
                    {l.jobId ? (
                      <Link href={`/jobs/${l.jobId}`} className="text-xs text-brandorange-dark hover:underline">
                        {jobTitle.get(l.jobId) ?? "Job"}
                      </Link>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-ink">{money(l.extendedCents)}</span>
                </div>
              ))}
              {live.surcharges.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 py-1.5">
                  <span className="text-ink">
                    {s.label} <span className="text-xs text-steel">· {SURCHARGE_LABEL[s.type]}</span>
                  </span>
                  <span className="text-ink">{money(s.amountCents)}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-right text-ink">
              Total <strong>{money(live.totalCents)}</strong>
            </p>
            {live.note ? <p className="mt-1 text-xs text-steel">{live.note}</p> : null}
            <div className="mt-2 flex items-center gap-3">
              {canEdit ? (
                <button className="text-xs text-brandorange-dark hover:underline" onClick={() => setEditing(true)}>
                  Edit
                </button>
              ) : null}
              {isAdmin || (mine && live.status === "draft") ? (
                <ConfirmButton
                  className="px-2 py-1 text-xs"
                  confirm={inQb ? "Delete here? The bill stays in QuickBooks." : "Delete this expense?"}
                  confirmLabel="Delete"
                  onConfirm={() => act("deleteExpense", { confirmInQuickBooks: inQb }, onBack)}
                >
                  Delete
                </ConfirmButton>
              ) : null}
            </div>
          </div>
        )}
        {message ? <p className="mt-2 text-xs text-danger">{message}</p> : null}
      </Card>

      {isManager && targets.length > 0 ? (
        <Card>
          <ExpenseQbPanel tenantId={tenantId} expenseId={live.id} expense={live} targets={[...targets]} />
        </Card>
      ) : null}
    </div>
  );
}
